import React, { useEffect, useRef } from "react";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const SpaceJetParticleField = ({
  className = "",
  minParticles = 18,
  maxParticles = 54,
  particleArea = 36000,
  speed = 0.14,
  baseOpacity = 0.2,
  interactionRadius = 180,
  nodeX = 0.62,
  nodeY = 0.38,
  nodeRadius = 240,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === "undefined") return;

    const parent = canvas.parentElement;
    let restoreParentPosition = null;
    if (parent) {
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.position === "static") {
        restoreParentPosition = parent.style.position;
        parent.style.position = "relative";
      }
    }

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const isTouchLike = window.matchMedia?.("(pointer: coarse)")?.matches || window.innerWidth < 768;

    if (prefersReducedMotion || isTouchLike) {
      return () => {
        if (parent && restoreParentPosition !== null) {
          parent.style.position = restoreParentPosition;
        }
      };
    }

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) {
      return () => {
        if (parent && restoreParentPosition !== null) {
          parent.style.position = restoreParentPosition;
        }
      };
    }

    let rafId;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let particles = [];

    const mouse = {
      x: 0,
      y: 0,
      active: false,
    };

    const random = (a, b) => a + Math.random() * (b - a);

    const createParticles = () => {
      const count = clamp(Math.floor((width * height) / particleArea), minParticles, maxParticles);
      particles = Array.from({ length: count }, () => ({
        x: random(0, width),
        y: random(0, height),
        vx: random(-1, 1) * speed,
        vy: random(-1, 1) * speed,
        r: random(0.7, 1.8),
        twinkle: random(0.2, 1),
        phase: random(0, Math.PI * 2),
      }));
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = clamp(window.devicePixelRatio || 1, 1, 1.5);

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      createParticles();
    };

    const moveMouse = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
      mouse.active = mouse.x >= 0 && mouse.x <= width && mouse.y >= 0 && mouse.y <= height;
    };

    const leaveMouse = () => {
      mouse.active = false;
    };

    const step = (time) => {
      ctx.clearRect(0, 0, width, height);

      const coreX = width * nodeX;
      const coreY = height * nodeY;

      // Network connections
      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i += 1) {
        const a = particles[i];

        for (let j = i + 1; j < particles.length; j += 1) {
          const b = particles[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 150) continue;

          const alpha = (1 - dist / 150) * baseOpacity * 0.55;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(148, 163, 184, ${alpha})`;
          ctx.stroke();
        }

        const coreDx = coreX - a.x;
        const coreDy = coreY - a.y;
        const coreDist = Math.sqrt(coreDx * coreDx + coreDy * coreDy);
        if (coreDist < nodeRadius) {
          const alpha = (1 - coreDist / nodeRadius) * (baseOpacity + 0.09);
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(coreX, coreY);
          ctx.strokeStyle = `rgba(96, 165, 250, ${alpha * 0.9})`;
          ctx.stroke();
        }
      }

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];

        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const distSq = dx * dx + dy * dy;
          const radiusSq = interactionRadius * interactionRadius;

          if (distSq < radiusSq) {
            const dist = Math.sqrt(distSq) || 1;
            const pull = (1 - dist / interactionRadius) * 0.028;
            p.vx += (dx / dist) * pull;
            p.vy += (dy / dist) * pull;
          }
        }

        p.vx *= 0.985;
        p.vy *= 0.985;

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > width) {
          p.vx *= -1;
          p.x = clamp(p.x, 0, width);
        }
        if (p.y < 0 || p.y > height) {
          p.vy *= -1;
          p.y = clamp(p.y, 0, height);
        }

        const twinkle = 0.62 + Math.sin(time * 0.0008 * p.twinkle + p.phase) * 0.38;
        const alpha = baseOpacity * twinkle;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(203, 213, 225, ${alpha * 0.92})`;
        ctx.fill();
      }

      if (mouse.active) {
        const glow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, interactionRadius);
        glow.addColorStop(0, "rgba(148, 163, 184, 0.12)");
        glow.addColorStop(0.6, "rgba(96, 165, 250, 0.04)");
        glow.addColorStop(1, "rgba(96, 165, 250, 0)");
        ctx.fillStyle = glow;
        ctx.fillRect(mouse.x - interactionRadius, mouse.y - interactionRadius, interactionRadius * 2, interactionRadius * 2);
      }

      const corePulse = 0.68 + Math.sin(time * 0.0012) * 0.1;
      const coreGlow = ctx.createRadialGradient(coreX, coreY, 0, coreX, coreY, 86);
      coreGlow.addColorStop(0, `rgba(248, 250, 252, ${0.84 * corePulse})`);
      coreGlow.addColorStop(0.28, `rgba(148, 163, 184, ${0.48 * corePulse})`);
      coreGlow.addColorStop(0.64, `rgba(194, 65, 65, ${0.14 * corePulse})`);
      coreGlow.addColorStop(1, 'rgba(194, 65, 65, 0)');
      ctx.fillStyle = coreGlow;
      ctx.beginPath();
      ctx.arc(coreX, coreY, 86, 0, Math.PI * 2);
      ctx.fill();

      ctx.save();
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.24)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(coreX, coreY, 52 + Math.sin(time * 0.0012) * 3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      ctx.beginPath();
      ctx.arc(coreX, coreY, 12, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(coreX, coreY, 6, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(194, 65, 65, 0.92)';
      ctx.fill();

      rafId = window.requestAnimationFrame(step);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", moveMouse, { passive: true });
    window.addEventListener("mouseleave", leaveMouse);
    window.addEventListener("blur", leaveMouse);
    rafId = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", moveMouse);
      window.removeEventListener("mouseleave", leaveMouse);
      window.removeEventListener("blur", leaveMouse);
      if (parent && restoreParentPosition !== null) {
        parent.style.position = restoreParentPosition;
      }
    };
  }, [baseOpacity, interactionRadius, maxParticles, minParticles, nodeRadius, nodeX, nodeY, particleArea, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={`spacejet-particle-layer ${className}`.trim()}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
        mixBlendMode: "screen",
      }}
    />
  );
};

export default SpaceJetParticleField;
